import {observable, action, runInAction, computed} from "mobx";
import {IApiClient} from "../common/api.client";
import {AnswerSheetDataDto, AnswerSheetDto} from "../common/webapicall";
import {ChartTypes} from "../public/report-page/types/report.chart.types";
import {ReportSelectorDropdownOption} from "../../src/public/report-page/types/report.selector.dropdown.option";
import {getUserAnswerSheetSelectorFormat} from "../utils/date.utils";
import {IConfigurationProvider} from "../common/public.configuration";

type AnswerSheetsListItem = {
	[key: string]: AnswerSheetDataDto;
};

export class ReportPageStore {
	constructor(private apiClient: IApiClient, private configurationProvider: IConfigurationProvider) {}

	@observable isAnswerSheetDetailsLoading: boolean = true;
	questionnaireAnswerSheetDetailsItem: AnswerSheetsListItem = {};
	@observable questionnaireAnswerSheetDetails: AnswerSheetDataDto = null;
	@observable userQuestionnaireAnswerSheets: AnswerSheetDto[] = null;
	@observable selectedAnswerSheetId: string;
	@observable reportChartType: ChartTypes = ChartTypes.BarChart;

	@action
	setSelectedAnswerSheetId(answerSheetId: string) {
		this.selectedAnswerSheetId = answerSheetId;
	}

	@action
	async getUserQuestionnaireAnswerSheets() {
		const response = await this.apiClient.questionnaireAnswerClient.getUserAnswerSheets();
		runInAction(() => {
			if (response) {
				this.userQuestionnaireAnswerSheets = response.sort((a, b) => (a.answerSheetCreated > b.answerSheetCreated ? 1 : b.answerSheetCreated > a.answerSheetCreated ? -1 : 0));
			}
		});
	}

	@action
	async getNewestUserQuestionnaireAnswerDetails() {
		const newestAnswerSheetId = this.userQuestionnaireAnswerSheets[this.userQuestionnaireAnswerSheets.length - 1].answerSheetId;
		await this.getUserQuestionnaireAnswerDetails(newestAnswerSheetId);
		return newestAnswerSheetId;
	}

	@action
	async getUserQuestionnaireAnswerDetails(answerSheetId: string) {
		this.setSelectedAnswerSheetId(answerSheetId);
		this.isAnswerSheetDetailsLoading = true;
		if (this.questionnaireAnswerSheetDetailsItem[answerSheetId]) {
			this.questionnaireAnswerSheetDetails = this.questionnaireAnswerSheetDetailsItem[answerSheetId];
		} else {
			const response = await this.apiClient.questionnaireAnswerClient.getUserAnswerSheetDetails(answerSheetId);
			runInAction(() => {
				if (response) {
					this.questionnaireAnswerSheetDetails = response;
					this.questionnaireAnswerSheetDetailsItem[answerSheetId] = response;
				}
			});
		}
		this.isAnswerSheetDetailsLoading = false;
	}

	@action
	setReportChartType(chartType: ChartTypes) {
		this.reportChartType = chartType;
	}

	@action
	async getExportDownloadUrl(answerSheetId: string) {
		if (!this.questionnaireAnswerSheetDetails.pdfDownload.downloadLink) {
			setTimeout(async () => {
				const res = await this.apiClient.questionnaireAnswerClient.getExportDownloadUrl(answerSheetId);
				this.questionnaireAnswerSheetDetails.pdfDownload.downloadLink = res.downloadLink;
				this.questionnaireAnswerSheetDetails.pdfDownload.fileSize = res.fileSize;

				if (this.questionnaireAnswerSheetDetailsItem[answerSheetId]) {
					this.questionnaireAnswerSheetDetailsItem[answerSheetId].pdfDownload.downloadLink = res.downloadLink;
					this.questionnaireAnswerSheetDetailsItem[answerSheetId].pdfDownload.fileSize = res.fileSize;
				}
			}, this.configurationProvider.configuration.exportPdfRefreshIntervalInSec * 1000);
		}
	}

	@computed get numberOfAnswerSheets() {
		return this.userQuestionnaireAnswerSheets?.length ? this.userQuestionnaireAnswerSheets?.length : 0;
	}

	@computed get answerSheetsSelectOptions() {
		const options: ReportSelectorDropdownOption[] = this.userQuestionnaireAnswerSheets.map((answerSheet) => {
			return {
				answerSheetCreated: getUserAnswerSheetSelectorFormat(answerSheet.answerSheetCreated),
				answerSheetId: answerSheet.answerSheetId,
			};
		});
		return options;
	}

	@computed get taxonomyOrderedAnswerSheetDetails() {
		return this.questionnaireAnswerSheetDetails.results.sort((a, b) => (a.taxonomySequentialId > b.taxonomySequentialId ? 1 : b.taxonomySequentialId > a.taxonomySequentialId ? -1 : 0));
	}

	@computed get taxonomyOrderedAnswerByScore() {
		return this.questionnaireAnswerSheetDetails.results.sort((a, b) => b.score - a.score);
	}
}
